import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Config from "../../_config/config";

interface RouteParams {
  slug: string;
}

const MANUALS: Record<string, { title: string }> = {
  scheduling: { title: "Scheduling — User Manual" },
  "flip-class": { title: "FlipClass — User Manual" },
};

const ManualViewer: React.FC = () => {
  const { slug } = useParams<RouteParams>();
  const [srcDoc, setSrcDoc] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const manual = MANUALS[slug];
    if (!manual) {
      setError(`Unknown manual: ${slug}`);
      return;
    }
    document.title = manual.title;

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/manuals/${slug}/${slug}.html`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const raw = await resp.text();
        // Don't inject <base> — it makes `<a href="#anchor">` resolve to a
        // different URL than `about:srcdoc`, which causes the iframe to
        // reload on every TOC click instead of scrolling in-page. Instead,
        // rewrite the relative paths we actually care about to absolute.
        const absImages = raw.replace(
          /(src|href)="images\//g,
          `$1="/manuals/${slug}/images/`
        );
        // Cross-manual link (flip-class → scheduling). Force target="_top"
        // so the parent SPA navigates to the other manual's viewer route.
        const absCross = absImages.replace(
          /href="\.\.\/timecraft\/scheduling-user-manual\.html"/g,
          'href="/manuals/scheduling" target="_top"'
        );
        const tenant = (Config.site || "").replace(/[^a-z0-9-]/gi, "");
        const substituted = absCross.replace(/__TENANT__/g, tenant);
        // srcDoc iframes update the URL hash on anchor clicks but the browser
        // doesn't reliably scroll the target into view. Inject a small handler
        // that does scrollIntoView explicitly for in-page links.
        const anchorPatch = `
<script>
(function(){
  document.addEventListener('click', function(e){
    var a = e.target && e.target.closest && e.target.closest('a[href^="#"]');
    if (!a) return;
    var id = a.getAttribute('href').slice(1);
    if (!id) return;
    var el = document.getElementById(id);
    if (!el) return;
    e.preventDefault();
    el.scrollIntoView({behavior: 'smooth', block: 'start'});
    try { history.replaceState(null, '', '#' + id); } catch(_) {}
  }, false);
})();
</script>`;
        const withPatch = substituted.replace(/<\/body>/i, anchorPatch + "</body>");
        if (!cancelled) setSrcDoc(withPatch);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load manual");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (error) {
    return (
      <div style={{ padding: 24, fontFamily: "system-ui, sans-serif" }}>
        <h2>Could not load manual</h2>
        <p>{error}</p>
      </div>
    );
  }

  return (
    <iframe
      title={MANUALS[slug]?.title || "User Manual"}
      srcDoc={srcDoc || ""}
      style={{
        position: "fixed",
        inset: 0,
        width: "100vw",
        height: "100vh",
        border: "none",
        background: "var(--surface-card)",
      }}
    />
  );
};

export default ManualViewer;
